import { useState, useEffect, useCallback } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { LifeBuoy, Send, Plus, Bug, Lightbulb, Building2 } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const TYPE_L = { fel: { tk: "type_fel", icon: Bug, cls: "bg-red-100 text-red-700" }, forbattring: { tk: "type_forbattring", icon: Lightbulb, cls: "bg-amber-100 text-amber-800" } };
const STATUS = { "öppen": { tk: "st_oppen", v: "outline" }, "pågår": { tk: "st_pagar", v: "default" }, "löst": { tk: "st_lost", v: "secondary" } };
const STATUS_OPTS = ["öppen", "pågår", "löst"];

function Thread({ ticket, isMaster, canReply, onStatus }) {
  const { t } = useLang();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [tpls, setTpls] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get(`/support/tickets/${ticket.id}/messages`); setMsgs(r.data); } catch (e) { toast.error(apiErr(e)); }
  }, [ticket.id]);
  const loadT = useCallback(async () => {
    if (!isMaster) return;
    try { const r = await api.get("/support/templates"); setTpls(r.data); } catch (e) { /* ignore */ }
  }, [isMaster]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadT(); }, [loadT]);
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/support/tickets/${ticket.id}/messages`, { body: text }); setText(""); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const saveTpl = async () => {
    try { await api.post("/support/templates", { text: text.trim() }); loadT(); toast.success(t("saved_quick")); } catch (e) { toast.error(apiErr(e)); }
  };
  const delTpl = async (id) => { try { await api.delete(`/support/templates/${id}`); loadT(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="p-4" data-testid="ticket-thread">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-head text-base font-semibold">{ticket.title}</span>
        <Badge variant={STATUS[ticket.status]?.v}>{STATUS[ticket.status] ? t(STATUS[ticket.status].tk) : ticket.status}</Badge>
        {isMaster && (
          <Select value={ticket.status} onValueChange={onStatus}>
            <SelectTrigger className="ml-auto w-32" data-testid="ticket-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{t(STATUS[s].tk)}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <div className="max-h-[45vh] space-y-2 overflow-y-auto py-2">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.author_side === "master" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.author_side === "master" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              <div className="mb-0.5 text-[11px] opacity-70">{m.author_side === "master" ? t("master_admin") : m.author_name} · {fDateTime(m.created_at)}</div>
              <span className="whitespace-pre-wrap">{m.body}</span>
            </div>
          </div>
        ))}
      </div>
      {(isMaster || canReply) && (
        <div className="mt-2">
          {isMaster && (
            <div className="mb-2 flex flex-wrap items-center gap-1" data-testid="tpl-bar">
              {(tpls || []).map((tp) => (
                <span key={tp.id} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs">
                  <button onClick={() => setText((v) => (v ? `${v} ${tp.text}` : tp.text))} data-testid={`tpl-${tp.id}`}>{tp.text.length > 30 ? `${tp.text.slice(0, 30)}…` : tp.text}</button>
                  <button onClick={() => delTpl(tp.id)} className="text-muted-foreground hover:text-red-500" data-testid={`tpl-del-${tp.id}`}>×</button>
                </span>
              ))}
              {text.trim() && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={saveTpl} data-testid="tpl-save">{t("save_quick")}</Button>}
            </div>
          )}
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("reply_ph")} data-testid="ticket-reply-input" />
            <Button size="icon" onClick={send} data-testid="ticket-reply-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TicketList({ tickets, selected, onSelect, showOrg }) {
  const { t } = useLang();
  if (tickets.length === 0) return <EmptyState icon={LifeBuoy} title={t("no_tickets")} text={t("no_tickets_text")} />;
  return (
    <div className="space-y-2">
      {tickets.map((tk) => {
        const ty = TYPE_L[tk.type] || TYPE_L.fel; const Icon = ty.icon;
        return (
          <button key={tk.id} onClick={() => onSelect(tk)} data-testid={`ticket-${tk.id}`}
            className={`w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted ${selected?.id === tk.id ? "border-accent bg-accent/5" : ""}`}>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${ty.cls}`}><Icon className="h-3 w-3" />{t(ty.tk)}</span>
              <span className="truncate text-sm font-medium">{tk.title}</span>
              <Badge variant={STATUS[tk.status]?.v} className="ml-auto">{STATUS[tk.status] ? t(STATUS[tk.status].tk) : tk.status}</Badge>
            </div>
            {showOrg && <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="h-3 w-3" />{tk.company_name}</div>}
          </button>
        );
      })}
    </div>
  );
}

function MasterInbox() {
  const { t } = useLang();
  const { data: orgs } = useApi("/support/orgs");
  const [org, setOrg] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [tickets, setTickets] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => { if (orgs && orgs.length && !org) setOrg(orgs[0].id); }, [orgs, org]);
  const load = useCallback(async () => {
    if (!org) return;
    const p = new URLSearchParams({ company_id: org }); if (fStatus) p.set("status", fStatus); if (fType) p.set("type", fType);
    try { const r = await api.get(`/support/tickets?${p.toString()}`); setTickets(r.data); setSel(null); } catch (e) { toast.error(apiErr(e)); }
  }, [org, fStatus, fType]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (s) => {
    try { await api.patch(`/support/tickets/${sel.id}/status`, { status: s }); setSel((x) => ({ ...x, status: s })); load(); toast.success(t("status_updated")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title={t("sup_master_title")} icon={LifeBuoy} subtitle={t("sup_master_sub")} />
      <div className="mb-4 flex flex-wrap gap-2 border-b pb-2">
        {(orgs || []).map((o) => (
          <button key={o.id} onClick={() => setOrg(o.id)} data-testid={`org-tab-${o.id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${org === o.id ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>
            {o.name} {o.open > 0 && <span className="ml-1 rounded-full bg-red-600 px-1.5 text-[10px] text-white">{o.open}</span>}
          </button>
        ))}
        {(orgs || []).length === 0 && <span className="text-sm text-muted-foreground">{t("no_orgs")}</span>}
      </div>
      <div className="mb-3 flex gap-2">
        <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder={t("th_status")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("all_statuses")}</SelectItem>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{t(STATUS[s].tk)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fType || "all"} onValueChange={(v) => setFType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="filter-type"><SelectValue placeholder={t("type_lbl")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("all_types")}</SelectItem><SelectItem value="fel">{t("type_fel")}</SelectItem><SelectItem value="forbattring">{t("type_forbattring")}</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <TicketList tickets={tickets} selected={sel} onSelect={setSel} showOrg={false} />
        {sel && <Thread ticket={sel} isMaster canReply onStatus={setStatus} />}
      </div>
    </div>
  );
}

function OrgSupport({ canCreate }) {
  const { t } = useLang();
  const { data: tickets, reload } = useApi("/support/tickets");
  const [form, setForm] = useState({ type: "fel", title: "", description: "" });
  const [sel, setSel] = useState(null);
  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error(t("fill_title_desc"));
    try { await api.post("/support/tickets", form); toast.success(t("ticket_sent")); setForm({ type: "fel", title: "", description: "" }); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <PageHeader title={t("sup_title")} icon={LifeBuoy} subtitle={t("sup_sub")} />
      {canCreate ? (
        <Card className="mb-6 grid gap-3 p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="new-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fel">{t("type_fel")}</SelectItem><SelectItem value="forbattring">{t("improvement_suggestion")}</SelectItem></SelectContent>
            </Select>
            <Input className="sm:col-span-2" placeholder={t("title_ph")} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="new-title" />
          </div>
          <Textarea rows={3} placeholder={t("desc_ph2")} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="new-desc" />
          <Button className="justify-self-start" onClick={create} data-testid="new-submit"><Plus className="mr-1 h-4 w-4" />{t("send_ticket")}</Button>
        </Card>
      ) : (
        <Card className="mb-6 p-4 text-sm text-muted-foreground">{t("not_support_contact")}</Card>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <TicketList tickets={tickets || []} selected={sel} onSelect={setSel} showOrg={false} />
        {sel && <Thread ticket={sel} isMaster={false} canReply={canCreate} />}
      </div>
    </div>
  );
}

export default function Support() {
  const { t } = useLang();
  const { data: cap } = useApi("/support/capability");
  if (!cap) return <p className="text-muted-foreground">{t("loading")}</p>;
  return cap.is_master ? <MasterInbox /> : <OrgSupport canCreate={cap.can_create} />;
}
